import { DisabledByDefault } from "@mui/icons-material";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import CancelButton from "../cancelButton/CancelButton";
import TagCategoryColumns from "./TagCategoryColumns";
import type { SelectedTag } from "./TagSelect";
import TagSelectField from "./TagSelectField";

/** Tags a customer must have (`included`) and tags it must not have (`excluded`). */
export interface TagFilter {
  readonly included: SelectedTag[];
  readonly excluded: SelectedTag[];
}

type TagFilterState = "unset" | "included" | "excluded";

const NEXT_STATE: Record<TagFilterState, TagFilterState> = {
  unset: "included",
  included: "excluded",
  excluded: "unset",
};

interface TagFilterDialogProps {
  readonly value: TagFilter;
  readonly availableTags: Record<string, string[]>;
  readonly onSave: (value: TagFilter) => void;
  readonly onClose: () => void;
}

const isSameTag = (tag: SelectedTag, other: SelectedTag) =>
  tag.category === other.category && tag.value === other.value;

function stateOf(filter: TagFilter, tag: SelectedTag): TagFilterState {
  if (filter.included.some((included) => isSameTag(included, tag))) {
    return "included";
  } else if (filter.excluded.some((excluded) => isSameTag(excluded, tag))) {
    return "excluded";
  } else {
    return "unset";
  }
}

function withState(filter: TagFilter, tag: SelectedTag, state: TagFilterState): TagFilter {
  const included = filter.included.filter((other) => !isSameTag(other, tag));
  const excluded = filter.excluded.filter((other) => !isSameTag(other, tag));
  return {
    included: state === "included" ? [...included, tag] : included,
    excluded: state === "excluded" ? [...excluded, tag] : excluded,
  };
}

/** Each tag cycles through unset, "yes" (checked) and "no" (red cross) on click. */
const TagFilterDialog: FC<TagFilterDialogProps> = ({ value, availableTags, onSave, onClose }) => {
  const [draft, setDraft] = useState(value);
  return (
    <Dialog open onClose={onClose} maxWidth={false}>
      <DialogTitle>Tagi</DialogTitle>
      <DialogContent>
        <TagCategoryColumns
          availableTags={availableTags}
          renderControl={(category, tagValue) => {
            const tag = { category, value: tagValue };
            const state = stateOf(draft, tag);
            return (
              <Checkbox
                checked={state === "included"}
                indeterminate={state === "excluded"}
                indeterminateIcon={<DisabledByDefault color="error" />}
                onChange={() => setDraft((previous) => withState(previous, tag, NEXT_STATE[state]))}
              />
            );
          }}
        />
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <Button color="primary" onClick={() => onSave(draft)}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface Props {
  readonly label?: string;
  readonly value: TagFilter;
  readonly availableTags: Record<string, string[]>;
  readonly maxVisibleTags?: number;
  readonly onChange: (value: TagFilter) => void;
}

/** Tag picker for filters: every tag can be required, excluded or left unset. */
const TagFilterSelect: FC<Props> = ({ label = "Tagi", value, availableTags, maxVisibleTags = 5, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <>
      <TagSelectField
        label={label}
        tags={value.included}
        excludedTags={value.excluded}
        maxVisibleTags={maxVisibleTags}
        onOpen={() => setDialogOpen(true)}
      />
      {dialogOpen && (
        <TagFilterDialog
          value={value}
          availableTags={availableTags}
          onClose={() => setDialogOpen(false)}
          onSave={(nextValue) => {
            onChange(nextValue);
            setDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

export default TagFilterSelect;
